import React, { useMemo, useState } from "react";
import classNames from "classnames";
import { Button } from "@design-system/Button/Button";
import { usePersonalAccessTokensQuery } from "@api/personalAccessTokens.api";
import { Section } from "@design-system/Section/Section";
import { TablePageHeader } from "@components/pageHeader/TablePageHeader";
import { CreatedTokenModal } from "@design-system/CreatedTokenModal/CreatedTokenModal";
import { ArrowCornerIcon } from "@icons";
import { SearchFilter } from "@design-system/SearchFilter/SearchFilter";
import { ToggleGroup } from "@design-system/ToggleGroup/ToggleGroup";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { CreatePersonalAccessTokenModal } from "./CreatePersonalAccessTokenModal";
import { McpCliTokensView } from "./McpCliTokensView";
import { ConnectorsCatalog } from "./ConnectorsCatalog";
import {
    CategoryFilter,
    ConnectorCategory,
    getAvailableCategories,
} from "./connectorDefinitions";
import { formatPermissions, tokenStatus } from "./mcpCliTokenHelpers";
import { mcpEndpointUrl } from "./mcpEndpoint";
import styles from "./McpCliSettings.module.scss";

type ConnectorsTab = "connectors" | "manage";

const LEARN_MORE_URL = "https://docs.kick.co/ai/mcp/mcp-quickstart";

const PersonalAccessTokensSection: React.FC = () => {
    const { data, isLoading } = usePersonalAccessTokensQuery();
    const [isCreating, setIsCreating] = useState(false);
    const [search, setSearch] = useState("");
    const [createdPlaintext, setCreatedPlaintext] = useState<string | null>(
        null,
    );

    const tokens = useMemo(() => data ?? [], [data]);
    const filteredTokens = useMemo(() => {
        if (!search) {
            return tokens;
        }

        const normalizedSearch = search.toLowerCase();
        return tokens.filter((token) =>
            [
                token.name,
                token.prefix,
                formatPermissions(token.permissions),
                tokenStatus(token),
            ].some((value) => value.toLowerCase().includes(normalizedSearch)),
        );
    }, [search, tokens]);

    const tableData = isLoading ? null : filteredTokens;

    return (
        <Section
            heading="Personal access tokens"
            subheading="Personal access tokens grant programmatic access to Kick as you. Use them with the Kick API."
        >
            {createdPlaintext && (
                <CreatedTokenModal
                    plaintext={createdPlaintext}
                    onDismiss={() => setCreatedPlaintext(null)}
                />
            )}

            <CreatePersonalAccessTokenModal
                show={isCreating}
                onCreated={(plaintext) => {
                    setCreatedPlaintext(plaintext);
                    setIsCreating(false);
                }}
                onCancel={() => setIsCreating(false)}
            />

            <TablePageHeader search={search} onSearchChange={setSearch}>
                <Button
                    variant="primary"
                    size="md"
                    onClick={() => setIsCreating(true)}
                    data-testid="personal-access-token-create-button"
                >
                    Create token
                </Button>
            </TablePageHeader>

            <McpCliTokensView data={tableData} />
        </Section>
    );
};

const RemoteMcpSection: React.FC = () => {
    const endpoint = mcpEndpointUrl();

    return (
        <Section
            heading="Remote MCP"
            subheading={
                <>
                    Connect MCP clients directly to Kick with Streamable HTTP.
                    Use a personal access token for PAT auth, or an OAuth client
                    with <code className={styles.inlineCode}>mcp:read</code> and{" "}
                    <code className={styles.inlineCode}>mcp:write</code> scopes.
                </>
            }
        >
            <div className={styles.remoteMcpCard}>
                <div className={styles.configRow}>
                    <span className={styles.configLabel}>Endpoint</span>
                    <code className={styles.inlineCode}>{endpoint}</code>
                </div>
                <div className={styles.configRow}>
                    <span className={styles.configLabel}>Header</span>
                    <code className={styles.inlineCode}>
                        Authorization: Bearer kick_pat_replace_me
                    </code>
                </div>
            </div>
        </Section>
    );
};

const ManageSection: React.FC = () => (
    <>
        <RemoteMcpSection />
        <PersonalAccessTokensSection />
    </>
);

export const McpCliSettings: React.FC = () => {
    const [tab, setTab] = useState<ConnectorsTab>("connectors");
    const [search, setSearch] = useState("");
    const [category, setCategory] = useState<CategoryFilter>("all");
    const { googleSheetsConnector } = useTypedFlags();

    const availableCategories = useMemo(
        () => getAvailableCategories({ googleSheetsConnector }),
        [googleSheetsConnector],
    );

    const activeCategory: CategoryFilter =
        category === "all" || availableCategories.has(category)
            ? category
            : "all";

    return (
        <div className="settings__mcp-cli">
            <header className="settings__header">
                <h3>Connectors</h3>
            </header>

            <p className={styles.pageSubtitle}>
                Connect Kick to your AI tools to automate your accounting{" "}
                <a
                    className={styles.learnMore}
                    href={LEARN_MORE_URL}
                    target="_blank"
                    rel="noreferrer"
                >
                    AI & Automation at Kick
                    <ArrowCornerIcon />
                </a>
            </p>

            <div className={styles.toolbarCard}>
                <nav className={styles.tabs}>
                    <button
                        type="button"
                        className={classNames(styles.tab, {
                            [styles.tabActive]: tab === "connectors",
                        })}
                        onClick={() => setTab("connectors")}
                    >
                        Connectors
                    </button>
                    <button
                        type="button"
                        className={classNames(styles.tab, {
                            [styles.tabActive]: tab === "manage",
                        })}
                        onClick={() => setTab("manage")}
                    >
                        Manage
                    </button>
                </nav>

                {tab === "connectors" && (
                    <div className={styles.connectorsToolbar}>
                        <SearchFilter
                            filter={search}
                            onChange={(value) => setSearch(value ?? "")}
                            minLength={1}
                            placeholder="Search..."
                            dataTestId="connectors-search"
                        />
                        <ToggleGroup.Root<CategoryFilter>
                            value={activeCategory}
                            onValueChange={(value) =>
                                setCategory(value ?? "all")
                            }
                            className={styles.filterChips}
                        >
                            <ToggleGroup.Item
                                value="all"
                                className={styles.filterChip}
                            >
                                All
                            </ToggleGroup.Item>
                            {availableCategories.has(
                                ConnectorCategory.AI_TOOLS,
                            ) && (
                                <ToggleGroup.Item
                                    value={ConnectorCategory.AI_TOOLS}
                                    className={styles.filterChip}
                                >
                                    AI tools
                                </ToggleGroup.Item>
                            )}
                            {availableCategories.has(
                                ConnectorCategory.DEVELOPER_TOOLS,
                            ) && (
                                <ToggleGroup.Item
                                    value={ConnectorCategory.DEVELOPER_TOOLS}
                                    className={styles.filterChip}
                                >
                                    Developer tools
                                </ToggleGroup.Item>
                            )}
                            {availableCategories.has(
                                ConnectorCategory.PRODUCTIVITY_TOOLS,
                            ) && (
                                <ToggleGroup.Item
                                    value={ConnectorCategory.PRODUCTIVITY_TOOLS}
                                    className={styles.filterChip}
                                >
                                    Productivity tools
                                </ToggleGroup.Item>
                            )}
                        </ToggleGroup.Root>
                    </div>
                )}
            </div>

            {tab === "connectors" ? (
                <ConnectorsCatalog search={search} category={activeCategory} />
            ) : (
                <ManageSection />
            )}
        </div>
    );
};
